/**
 * Maps URL segments after /portal/ to Cognito group names allowed to open that route.
 * Order matters: more specific paths must be checked before prefixes (e.g. medicine/create before medicine).
 */

/** Roles allowed to view /portal/order-medicine (list and related UI). */
export const ORDER_MEDICINE_VIEW_ROLES = [
  'SuperAdmin',
  'OperationsManager',
  'Prescription',
  'PrescriptionManager',
  'Fulfilment',
  'Viewer',
];

/** Roles allowed to open /portal/dashboard (Packaging uses prescription/shipping workflow only). */
export const PORTAL_DASHBOARD_ROLES = [
  'SuperAdmin',
  'OperationsManager',
  'PrescriptionManager',
  'Prescription',
  'Fulfilment',
  'Viewer',
];

/** Strip query/hash and return path after /portal (no leading slash). */
export function normalizePortalPath(fullUrl: string): string {
  const path = fullUrl.split(/[?#]/)[0];
  const m = path.match(/^\/portal(?:\/(.*))?$/);
  const rest = m && m[1] ? m[1] : '';
  return rest.replace(/\/+$/, '');
}

/**
 * @returns null = portal shell only (/portal) — allow any authenticated user
 * @returns string[] = user must have at least one of these Cognito groups
 */
export function getRequiredRolesForPortalPath(normalizedPath: string): string[] | null {
  const p = normalizedPath.replace(/^\/+|\/+$/g, '');
  if (!p) {
    return null;
  }

  if (p.startsWith('integrations')) {
    return ['SuperAdmin'];
  }
  if (p === 'logs') {
    return ['SuperAdmin'];
  }
  if (p === 'users/create') {
    return ['SuperAdmin', 'OperationsManager'];
  }
  if (p === 'users' || p.startsWith('users/view/')) {
    return ['SuperAdmin', 'OperationsManager'];
  }
  if (p === 'order-medicine/create') {
    return ['SuperAdmin', 'OperationsManager', 'Prescription', 'Fulfilment'];
  }
  if (p === 'medicine/create') {
    return ['SuperAdmin', 'OperationsManager', 'Prescription'];
  }
  if (p === 'submit' || p === 'prescriptions-form') {
    return ['SuperAdmin', 'OperationsManager', 'PrescriptionManager', 'Prescription'];
  }
  if (p === 'stocktake') {
    return ['SuperAdmin', 'OperationsManager', 'Fulfilment'];
  }
  if (p === 'stocktake/report') {
    return ['SuperAdmin', 'OperationsManager', 'Fulfilment', 'Viewer'];
  }
  if (p === 'stocktake/location') {
    return [
      'SuperAdmin',
      'OperationsManager',
      'Fulfilment',
      'PrescriptionManager',
      'Prescription',
      'Viewer',
    ];
  }
  if (p === 'batches/create') {
    return ['SuperAdmin', 'OperationsManager', 'PrescriptionManager', 'Fulfilment'];
  }
  if (p === 'individuals/create' || p === 'companies/create') {
    // PrescriptionManager: view individuals/companies only — no create
    return ['SuperAdmin', 'OperationsManager', 'Prescription'];
  }
  if (p.startsWith('split/')) {
    return [
      'SuperAdmin',
      'OperationsManager',
      'PrescriptionManager',
      'Prescription',
      'Fulfilment',
      'Packaging',
      'Viewer',
    ];
  }
  if (p.startsWith('fulfill/')) {
    return ['SuperAdmin', 'OperationsManager', 'Fulfilment', 'Packaging'];
  }
  if (p === 'dashboard') {
    return [...PORTAL_DASHBOARD_ROLES];
  }
  if (p === 'prescriptions' || p.startsWith('prescriptions/')) {
    return [
      'SuperAdmin',
      'OperationsManager',
      'PrescriptionManager',
      'Prescription',
      'Fulfilment',
      'Packaging',
      'Viewer',
    ];
  }
  if (p === 'shipping' || p.startsWith('shipping/')) {
    return [
      'SuperAdmin',
      'OperationsManager',
      'PrescriptionManager',
      'Prescription',
      'Fulfilment',
      'Packaging',
      'Viewer',
    ];
  }
  if (p === 'batches' || p.startsWith('batches/view/')) {
    return [
      'SuperAdmin',
      'OperationsManager',
      'PrescriptionManager',
      'Prescription',
      'Fulfilment',
      'Viewer',
    ];
  }
  if (p === 'individuals' || p.startsWith('individuals/view/')) {
    return ['SuperAdmin', 'OperationsManager', 'PrescriptionManager', 'Prescription', 'Viewer'];
  }
  if (p === 'companies' || p.startsWith('companies/view/')) {
    return ['SuperAdmin', 'OperationsManager', 'PrescriptionManager', 'Prescription', 'Viewer'];
  }
  if (p === 'medicine' || p.startsWith('medicine/view/')) {
    return [
      'SuperAdmin',
      'OperationsManager',
      'PrescriptionManager',
      'Prescription',
      'Fulfilment',
      'Viewer',
    ];
  }
  if (p === 'order-medicine' || p.startsWith('order-medicine/')) {
    return [...ORDER_MEDICINE_VIEW_ROLES];
  }

  // Unknown /portal/* path: staff only
  return [
    'SuperAdmin',
    'OperationsManager',
    'PrescriptionManager',
    'Prescription',
    'Fulfilment',
    'Packaging',
    'Viewer',
  ];
}
